import { useDroppable, DragOverlay } from '@dnd-kit/core'
import { SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { LayoutDashboard, Plus, Settings2 } from 'lucide-react'
import { useMemo, useRef, useState, useEffect, useCallback } from 'react'

import type { DashboardAnalyticsResponse, DashboardWidget } from '@/types/api/dashboard'
import { DashboardWidgetType } from '@/types/enums'

import { DashboardWidget as DashboardWidgetComponent } from './DashboardWidget'


interface DashboardGridProps {
  widgets: DashboardWidget[]
  widgetData: Record<string, DashboardAnalyticsResponse | undefined>
  isEditing?: boolean
  lockedRecordGroupId?: string
  onDeleteWidget?: (widgetId: string) => void
  onWidgetUpdate?: (widgetId: string, updates: Partial<DashboardWidget>) => void
  onResizeWidget?: (widgetId: string, w: number, h: number) => void
  onLoadMore?: (widgetId: string, dataSource: string, config: Record<string, unknown>) => Promise<void>
  activeDragId?: string | null
  paletteDragWidget?: DashboardWidget | null
}

const PALETTE_MOCK_DATA: Record<string, DashboardAnalyticsResponse> = {
  [DashboardWidgetType.NUMBER_CARD]: {
    metrics: [{ label: 'Total Sales', value: 12450 }],
  },
  [DashboardWidgetType.NUMBER_CARD_GROUP]: {
    metrics: [
      { label: 'Active', value: 12 },
      { label: 'Pending', value: 5 },
      { label: 'Closed', value: 89 },
    ],
  },
  [DashboardWidgetType.BAR_CHART]:
  { series: [
      { label: 'Mon', value: 10 },
      { label: 'Tue', value: 15 },
      { label: 'Wed', value: 8 },
    ] },
  [DashboardWidgetType.LINE_CHART]:
  { series: [
      { label: 'Mon', value: 10 },
      { label: 'Tue', value: 15 },
      { label: 'Wed', value: 8 },
    ] },
  [DashboardWidgetType.PIE_CHART]:
  { series: [
      { label: 'Mon', value: 10 },
      { label: 'Tue', value: 15 },
      { label: 'Wed', value: 8 },
    ] },
  [DashboardWidgetType.DONUT_CHART]:
  { series: [
      { label: 'Mon', value: 10 },
      { label: 'Tue', value: 15 },
      { label: 'Wed', value: 8 },
    ] },
  [DashboardWidgetType.STACKED_BAR]:
  { series: [
      { label: 'Mon', value: 10 },
      { label: 'Tue', value: 15 },
      { label: 'Wed', value: 8 },
    ] },
  [DashboardWidgetType.TABLE]: {
    items: [
      { id: '1', name: 'Alice', status: 'Active' } as never,
      { id: '2', name: 'Bob', status: 'Pending' } as never,
    ],
  },
  [DashboardWidgetType.ACTIVITY_FEED]: {
    items: [
      { id: '1', actorName: 'System', actionType: 'UPDATE', createdAt: new Date().toISOString() },
      { id: '2', actorName: 'Alice', actionType: 'CREATE', createdAt: new Date(Date.now() - 3600000).toISOString() },
    ],
  },
  [DashboardWidgetType.PROGRESS_BAR]: {
    usage: {
      workspaceId: 'mock',
      subscriptionStatus: 'ACTIVE',
      currentPeriodStart: null,
      currentPeriodEnd: null,
      usage: [{ featureKey: 'users', name: 'Users', currentUsage: 5, limitValue: 10, limitType: 'HARD' }],
    },
  },
  [DashboardWidgetType.STATUS_BADGE_GROUP]: {
    series: [
      { label: 'Open', value: 5 },
      { label: 'In Progress', value: 3 },
      { label: 'Done', value: 12 },
    ],
  },
}

const SUMMARY_TYPES = new Set<string>([
  DashboardWidgetType.NUMBER_CARD,
  DashboardWidgetType.NUMBER_CARD_GROUP,
  DashboardWidgetType.PROGRESS_BAR,
  DashboardWidgetType.STATUS_BADGE_GROUP,
])

interface WidgetLimit {
  minW: number
  minH: number
}

const WIDGET_LIMITS: Record<string, WidgetLimit> = {
  [DashboardWidgetType.NUMBER_CARD]: { minW: 2, minH: 2 },
  [DashboardWidgetType.NUMBER_CARD_GROUP]: { minW: 6, minH: 2 },
  [DashboardWidgetType.PROGRESS_BAR]: { minW: 3, minH: 2 },
  [DashboardWidgetType.STATUS_BADGE_GROUP]: { minW: 3, minH: 2 },
  [DashboardWidgetType.BAR_CHART]: { minW: 4, minH: 4 },
  [DashboardWidgetType.LINE_CHART]: { minW: 4, minH: 4 },
  [DashboardWidgetType.PIE_CHART]: { minW: 4, minH: 4 },
  [DashboardWidgetType.DONUT_CHART]: { minW: 4, minH: 4 },
  [DashboardWidgetType.STACKED_BAR]: { minW: 4, minH: 4 },
  [DashboardWidgetType.TABLE]: { minW: 4, minH: 4 },
  [DashboardWidgetType.ACTIVITY_FEED]: { minW: 4, minH: 4 },
}

function ResizeHandle({
  onResize,
  onResizeEnd,
}: {
  onResize: (dx: number, dy: number) => void
  onResizeEnd: () => void
}) {
  const isDragging = useRef(false)
  const startPos = useRef({ x: 0, y: 0 })

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    e.preventDefault()
    e.stopPropagation()
    isDragging.current = true
    startPos.current = { x: e.clientX, y: e.clientY }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'se-resize'
  }, [])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    if (!isDragging.current) return
    e.preventDefault()
    const dx = e.clientX - startPos.current.x
    const dy = e.clientY - startPos.current.y
    startPos.current = { x: e.clientX, y: e.clientY }
    onResize(dx, dy)
  }, [onResize])

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLSpanElement>) => {
    if (!isDragging.current) return
    isDragging.current = false
    ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
    onResizeEnd()
  }, [onResizeEnd])

  return (
    <span
      className="absolute bottom-1.5 right-1.5 z-30 flex h-5 w-5 cursor-se-resize items-center justify-center rounded-md border border-neutral-300 bg-white/90 shadow-sm hover:bg-primary-50 hover:border-primary-300 transition-colors select-none touch-none opacity-0 group-hover:opacity-100"
      title="Resize widget"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="text-neutral-400 pointer-events-none">
        <circle cx="6" cy="2" r="1" fill="currentColor" />
        <circle cx="6" cy="6" r="1" fill="currentColor" />
        <circle cx="2" cy="6" r="1" fill="currentColor" />
      </svg>
    </span>
  )
}

function getWidgetPixelSize(widget: DashboardWidget, gridWidth: number) {
  const limits = WIDGET_LIMITS[widget.type as DashboardWidgetType] || { minW: 3, minH: 2 }
  const w = Math.max(limits.minW, widget.w)
  const h = Math.max(limits.minH, widget.h)
  const width = Math.max(220, Math.floor((w / 12) * gridWidth) - 16)
  const height = h * 80
  return { width, height }
}

const WIDGET_TYPE_COLORS: Record<string, string> = {
  [DashboardWidgetType.NUMBER_CARD]: 'from-blue-500 to-indigo-500',
  [DashboardWidgetType.NUMBER_CARD_GROUP]: 'from-blue-500 to-indigo-500',
  [DashboardWidgetType.BAR_CHART]: 'from-blue-500 to-indigo-500',
  [DashboardWidgetType.LINE_CHART]: 'from-emerald-500 to-teal-500',
  [DashboardWidgetType.PIE_CHART]: 'from-purple-500 to-violet-500',
  [DashboardWidgetType.DONUT_CHART]: 'from-purple-500 to-violet-500',
  [DashboardWidgetType.STACKED_BAR]: 'from-indigo-500 via-primary-500 to-purple-500',
  [DashboardWidgetType.TABLE]: 'from-amber-500 to-orange-500',
  [DashboardWidgetType.ACTIVITY_FEED]: 'from-rose-500 to-pink-500',
  [DashboardWidgetType.PROGRESS_BAR]: 'from-cyan-500 to-blue-500',
  [DashboardWidgetType.STATUS_BADGE_GROUP]: 'from-teal-500 to-emerald-500',
}

function SortableWidget({
  widget,
  data,
  isEditing,
  lockedRecordGroupId,
  gridWidth,
  isActiveDrag,
  isAnyDragging,
  onDeleteWidget,
  onWidgetUpdate,
  onResizeWidget,
  onLoadMore,
}: {
  widget: DashboardWidget
  data?: DashboardAnalyticsResponse
  isEditing: boolean
  lockedRecordGroupId?: string
  gridWidth: number
  isActiveDrag: boolean
  isAnyDragging: boolean
  onDeleteWidget?: (widgetId: string) => void
  onWidgetUpdate?: (widgetId: string, updates: Partial<DashboardWidget>) => void
  onResizeWidget?: (widgetId: string, w: number, h: number) => void
  onLoadMore?: (widgetId: string, dataSource: string, config: Record<string, unknown>) => Promise<void>
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: widget.id,
    data: { type: 'Widget', widget },
  })

  const limits = WIDGET_LIMITS[widget.type as DashboardWidgetType] || { minW: 3, minH: 2 }
  const { width: baseWidth, height: baseHeight } = getWidgetPixelSize(widget, gridWidth)
  const [localSize, setLocalSize] = useState({ width: baseWidth, height: baseHeight })

  // Reset the live-resize size whenever the persisted size (or grid width) changes
  useEffect(() => {
    setLocalSize({ width: baseWidth, height: baseHeight })
  }, [baseWidth, baseHeight])

  const handleResize = useCallback((dx: number, dy: number) => {
    const minWidthPx = Math.max(220, Math.floor((limits.minW / 12) * gridWidth) - 16)
    const minHeightPx = limits.minH * 80
    setLocalSize(prev => ({
      width: Math.max(minWidthPx, Math.min(gridWidth - 16, prev.width + dx)),
      height: Math.max(minHeightPx, prev.height + dy),
    }))
  }, [gridWidth, limits.minW, limits.minH])

  const handleResizeEnd = useCallback(() => {
    const cols = Math.max(limits.minW, Math.min(12, Math.round((localSize.width / gridWidth) * 12)))
    const rows = Math.max(limits.minH, Math.round(localSize.height / 80))
    onResizeWidget?.(widget.id, cols, rows)
  }, [localSize, gridWidth, widget.id, limits.minW, limits.minH, onResizeWidget])

  const style: React.CSSProperties = {
    transform: isAnyDragging ? undefined : CSS.Transform.toString(transform),
    transition: isAnyDragging ? undefined : transition ?? 'transform 220ms cubic-bezier(0.2, 0, 0, 1)',
    width: isEditing ? `${localSize.width}px` : `calc(${(Math.max(limits.minW, widget.w) / 12) * 100}% - 1rem)`,
    height: `${isEditing ? localSize.height : baseHeight}px`,
    minWidth: `${Math.max(220, Math.floor((limits.minW / 12) * gridWidth) - 16)}px`,
    minHeight: `${limits.minH * 80}px`,
    flexShrink: 0,
    opacity: isActiveDrag ? 0 : 1,
    position: 'relative',
    willChange: isEditing ? 'transform' : 'auto',
    zIndex: isDragging ? 10 : 1,
  }

  const gradient = WIDGET_TYPE_COLORS[widget.type] ?? 'from-neutral-500 to-neutral-400'

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group animate-fade-in"
    >
      <div className="relative h-full w-full overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5 transition-all duration-200 hover:shadow-md">
        <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${gradient}`} />
        <div className="flex h-full w-full flex-col pt-1">
          <div className={isEditing ? 'select-none flex-1 min-h-0 flex flex-col' : 'flex-1 min-h-0 flex flex-col'}>
            <DashboardWidgetComponent
              widget={widget}
              data={data}
              isEditing={isEditing}
              lockedRecordGroupId={lockedRecordGroupId}
              onDelete={onDeleteWidget}
              onWidgetUpdate={onWidgetUpdate}
              onLoadMore={onLoadMore}
              dragHandleProps={{ ...attributes, ...listeners }}
            />
          </div>
        </div>
      </div>
      {widget.id.startsWith('temp-') && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-xl">
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs font-medium text-amber-700 shadow-sm">
            <Settings2 className="h-3.5 w-3.5" />
            Not configured — save settings to activate
          </div>
        </div>
      )}
      {isEditing && !isActiveDrag && (
        <ResizeHandle onResize={handleResize} onResizeEnd={handleResizeEnd} />
      )}
    </div>
  )
}

function DragOverlayWidget({
  widget,
  data,
  gridWidth,
}: {
  widget: DashboardWidget
  data?: DashboardAnalyticsResponse
  gridWidth: number
}) {
  const isSummary = SUMMARY_TYPES.has(widget.type)
  const { width, height } = getWidgetPixelSize(widget, gridWidth)
  const gradient = WIDGET_TYPE_COLORS[widget.type] ?? 'from-neutral-500 to-neutral-400'
  const overlayData = (!data && !widget.dataSource) ? (PALETTE_MOCK_DATA[widget.type] ?? data) : data

  return (
    <div
      style={{
        width,
        height: isSummary ? undefined : (height ?? 320),
        minHeight: isSummary ? 100 : undefined,
        minWidth: 220,
      }}
      className="overflow-hidden rounded-xl bg-white shadow-2xl ring-2 ring-primary-400/50 scale-[1.02] opacity-95"
    >
      <div className={`h-1 bg-gradient-to-r ${gradient}`} />
      <DashboardWidgetComponent
        widget={widget}
        data={overlayData}
        isEditing={true}
        dragHandleProps={{}}
      />
    </div>
  )
}

export function DashboardGrid({
  widgets,
  widgetData,
  isEditing = false,
  lockedRecordGroupId,
  onDeleteWidget,
  onWidgetUpdate,
  onResizeWidget,
  onLoadMore,
  activeDragId,
  paletteDragWidget,
}: DashboardGridProps) {
  const { setNodeRef, isOver } = useDroppable({ id: 'dashboard-grid-droppable' })
  const containerRef = useRef<HTMLDivElement>(null)
  const [gridWidth, setGridWidth] = useState(1200)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      setGridWidth(entry.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const sortedWidgets = useMemo(() => {
    return [...widgets].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
  }, [widgets])

  const activeWidget = activeDragId ? (sortedWidgets.find(w => w.id === activeDragId) ?? paletteDragWidget) : null

  if (widgets.length === 0) {
    return (
      <div
        ref={setNodeRef}
        className={`flex h-56 flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-all duration-300 ${
          isOver
            ? 'border-primary-400 bg-primary-50/80 scale-[1.01] shadow-lg shadow-primary-500/10'
            : 'border-neutral-200/70 bg-white/40'
        }`}
      >
        <div className="flex flex-col items-center gap-3">
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-300 ${
            isOver ? 'bg-primary-100 text-primary-600 scale-110' : 'bg-neutral-100 text-neutral-400'
          }`}>
            {isOver ? <Plus className="h-6 w-6" /> : <LayoutDashboard className="h-6 w-6" />}
          </div>
          <p className={`text-sm font-medium transition-colors duration-300 ${
            isOver ? 'text-primary-700' : 'text-neutral-500'
          }`}>
            {isOver ? 'Drop to add widget' : isEditing ? 'Drag a widget from the left panel' : 'No widgets configured yet'}
          </p>
          <p className="text-xs text-neutral-400">
            {isEditing ? 'Or click any widget in the library' : 'Switch to edit mode to customize'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="w-full">
      <div
        ref={setNodeRef}
        className={`rounded-2xl transition-all duration-150 ${isOver ? 'bg-primary-50/30 ring-2 ring-primary-300/50' : ''}`}
      >
        {isEditing && (
          <div className="mb-4 rounded-xl border border-primary-100 bg-gradient-to-r from-primary-50 to-indigo-50/50 px-4 py-2.5 text-sm text-primary-700 flex items-center justify-center gap-2 select-none shadow-sm">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-bold">i</span>
            Drag widgets by their handle to reorder &bull; Resize from the corner handle
          </div>
        )}
        <SortableContext items={sortedWidgets.map((w) => w.id)} strategy={rectSortingStrategy}>
          <div className="flex flex-wrap gap-4 items-start pb-20">
            {sortedWidgets.map((widget) => (
              <SortableWidget
                key={widget.id}
                widget={widget}
                data={widgetData[widget.id]}
                isEditing={isEditing}
                lockedRecordGroupId={lockedRecordGroupId}
                gridWidth={gridWidth}
                isActiveDrag={activeDragId === widget.id}
                isAnyDragging={!!activeDragId}
                onDeleteWidget={onDeleteWidget}
                onWidgetUpdate={onWidgetUpdate}
                onResizeWidget={onResizeWidget}
                onLoadMore={onLoadMore}
              />
            ))}
          </div>
        </SortableContext>
      </div>

      <DragOverlay dropAnimation={{
        duration: 200,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      }}>
        {activeWidget ? (
          <DragOverlayWidget
            widget={activeWidget}
            data={widgetData[activeWidget.id]}
            gridWidth={gridWidth}
          />
        ) : null}
      </DragOverlay>
    </div>
  )
}
